import { Contract } from '@ethersproject/contracts'
import {
  JSBI,
  Percent,
  Router,
  SwapParameters,
  Trade,
  TradeType,
} from '@pancakeswap/sdk'
import useActiveWeb3React from 'hooks/useActiveWeb3React'
import { useMemo } from 'react'
import {
  ROBINHOOD_MAINNET_CHAIN_ID,
  getRouterAddress,
} from 'config/constants/chains'
import {
  BIPS_BASE,
  INITIAL_ALLOWED_SLIPPAGE,
} from '../config/constants'
import {
  getAggregatorContract,
  getRouterContract,
} from '../utils'
import useTransactionDeadline from './useTransactionDeadline'

interface SwapCall {
  contract: Contract
  parameters: SwapParameters
}

export function useSwapCallArguments(
  trade: Trade | undefined,
  allowedSlippage: number = INITIAL_ALLOWED_SLIPPAGE,
  recipientAddress: string | null,
): SwapCall[] {
  const { account, chainId, library } = useActiveWeb3React()

  const recipient = recipientAddress === null ? account : recipientAddress
  const deadline = useTransactionDeadline()

  return useMemo(() => {
    if (
      !trade ||
      !recipient ||
      !library ||
      !account ||
      !chainId ||
      !deadline
    ) {
      console.error('SWAP CALL ARGUMENTS MISSING DEPENDENCY', {
        hasTrade: Boolean(trade),
        tradeType: trade?.tradeType,
        recipient,
        hasLibrary: Boolean(library),
        account,
        chainId,
        deadline: deadline?.toString(),
        recipientAddress,
      })

      return []
    }

    const routerAddress = getRouterAddress(chainId)

    if (!routerAddress) {
      return []
    }

    const standardParameters = Router.swapCallParameters(trade, {
      feeOnTransfer: false,
      allowedSlippage: new Percent(
        JSBI.BigInt(allowedSlippage),
        BIPS_BASE,
      ),
      recipient,
      deadline: deadline.toNumber(),
    })

    if (chainId === ROBINHOOD_MAINNET_CHAIN_ID) {
      // The deployed Aggregator currently supports exact-input swaps only.
      if (trade.tradeType !== TradeType.EXACT_INPUT) {
        return []
      }

      const aggregator = getAggregatorContract(library, account)

      // Aggregator deducts a 10 bps protocol fee from the gross input.
      // Reduce the router minimum output by the same amount so the
      // transaction is evaluated against the net routed input.
      const aggregatorArgs = [...standardParameters.args]

      const amountOutMinIndex =
        standardParameters.methodName === 'swapExactETHForTokens'
          ? 0
          : 1

      const originalAmountOutMin = JSBI.BigInt(
        aggregatorArgs[amountOutMinIndex],
      )

      const feeAdjustedAmountOutMin = JSBI.divide(
        JSBI.multiply(originalAmountOutMin, JSBI.BigInt(9990)),
        JSBI.BigInt(10000),
      )

      aggregatorArgs[amountOutMinIndex] =
        feeAdjustedAmountOutMin.toString()

      const aggregatorParameters: SwapParameters = {
        ...standardParameters,
        args: [routerAddress, ...aggregatorArgs],
      }

      return [
        {
          contract: aggregator,
          parameters: aggregatorParameters,
        },
      ]
    }

    const router = getRouterContract(chainId, library, account)
    const swapMethods: SwapParameters[] = [standardParameters]

    if (trade.tradeType === TradeType.EXACT_INPUT) {
      swapMethods.push(
        Router.swapCallParameters(trade, {
          feeOnTransfer: true,
          allowedSlippage: new Percent(
            JSBI.BigInt(allowedSlippage),
            BIPS_BASE,
          ),
          recipient,
          deadline: deadline.toNumber(),
        }),
      )
    }

    return swapMethods.map((parameters) => ({
      parameters,
      contract: router,
    }))
  }, [
    account,
    allowedSlippage,
    chainId,
    deadline,
    library,
    recipient,
    trade,
  ])
}
